import { createSlice } from '@reduxjs/toolkit'
import {
  createTeam,
  deleteTeam,
  adminDeleteTeam,
  followTeam,
  getAllTeams, getCaptainPanelData,
  getTeamDetails,
  handleTeamMember,
  leaveTeam,
  requestToJoinTeam,
  updateTeam,
} from './actions'
import { State } from './types'

const initialState: State = {
  isLoading: false,
  isCreateTeam: false,
  isHandlingMember: false,
  isGettingDetails: false,
  isDeletingTeam: false,
  newTeam: null,
  teams: [],
  teamMembers: {},
  currentTeamId: null,
  singleTeam: null,
  error: null,
  message: '',
  captainPanel: null,
  isLoadingCaptainPanel: false,
}

const teamSlice = createSlice({
  name: 'team',
  initialState,
  reducers: {
    teamMessageCleanUp(state) {
      state.message = ''
    },
    clearTeamError(state) {
      state.error = null
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAllTeams.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(getAllTeams.fulfilled, (state, { payload }) => {
        state.isLoading = false
        state.teams = payload.reverse()
      })
      .addCase(getAllTeams.rejected, (state, action) => {
        state.isLoading = false
        if (action.payload) {
          state.error = action.payload.errors || action.payload.message
        } else {
          state.error = action.error.message
        }
      })
      .addCase(createTeam.pending, (state) => {
        state.isCreateTeam = true
        state.error = null
      })
      .addCase(createTeam.fulfilled, (state, action) => {
        state.isCreateTeam = false
        state.newTeam = action.payload.team
        state.teams = [action.payload.team, ...state.teams]
        state.currentTeamId = action.payload.teamId
      })
      .addCase(createTeam.rejected, (state, action) => {
        state.isCreateTeam = false
        if (action.payload) {
          state.error = action.payload.errors || action.payload.message
        } else {
          state.error = action.error.message
        }
      })
      .addCase(handleTeamMember.pending, (state) => {
        state.isHandlingMember = true
        state.error = null
      })
      .addCase(handleTeamMember.fulfilled, (state, { payload }) => {
        state.isHandlingMember = false
        const teams = state.teams.filter((team) => team._id !== payload._id)
        state.teams = [...teams, payload]
      })
      .addCase(handleTeamMember.rejected, (state, action) => {
        state.isHandlingMember = false
        if (action.payload) {
          state.error = action.payload.errors || action.payload.message
        } else {
          state.error = action.error.message
        }
      })
      .addCase(getTeamDetails.pending, (state) => {
        state.isGettingDetails = true
        state.error = null
      })
      .addCase(getTeamDetails.fulfilled, (state, { payload }) => {
        state.isGettingDetails = false
        state.singleTeam = payload
        const teamId = payload.teamDetails._id
        state.teamMembers = {
          ...state.teamMembers,
          [teamId]: payload.teamMembersDetails,
        }
      })
      .addCase(getTeamDetails.rejected, (state, action) => {
        state.isGettingDetails = false
        // Silently handle 404 errors (team doesn't exist)
        // This can happen when a team is deleted but still referenced in tournaments or user profiles
        const is404 = action.payload?.message?.includes('does not exist') ||
                      action.payload?.message?.includes('404') ||
                      action.error?.message?.includes('404')

        if (!is404) {
          // Only show error notifications for non-404 errors
          if (action.payload) {
            state.error = action.payload.errors || action.payload.message
          } else {
            state.error = action.error.message
          }
        }
      })
      .addCase(followTeam.pending, (state) => {
        state.error = null
      })
      .addCase(followTeam.fulfilled, (state, { payload }) => {
        state.teams = state.teams.map((team) => {
          if (team._id === payload._id) {
            return payload
          }
          return team
        })
      })
      .addCase(followTeam.rejected, (state, action) => {
        if (action.payload) {
          state.error = action.payload.errors || action.payload.message
        } else {
          state.error = action.error.message
        }
      })
      .addCase(updateTeam.pending, (state) => {
        state.error = null
        state.isLoading = true
      })
      .addCase(updateTeam.fulfilled, (state, { payload }: any) => {
        state.isLoading = false
        // Handle both payload.team and payload.Team for backend compatibility
        const updatedTeam = payload.team || payload.Team
        if (updatedTeam) {
          const teams = state.teams.filter(
            (team) => team._id !== updatedTeam._id,
          )
          state.teams = [updatedTeam, ...teams]
          // Update singleTeam if it matches the updated team
          if (state.singleTeam?.teamDetails?._id === updatedTeam._id) {
            state.singleTeam = {
              ...state.singleTeam,
              teamDetails: updatedTeam,
            }
          }
        }
        state.message = payload.message
      })
      .addCase(updateTeam.rejected, (state, action) => {
        state.isLoading = false
        if (action.payload) {
          state.error = action.payload.errors || action.payload.message
        } else {
          state.error = action.error.message
        }
      })
      .addCase(requestToJoinTeam.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(requestToJoinTeam.fulfilled, (state, { payload }) => {
        state.isLoading = false
        state.message = payload.message
      })
      .addCase(requestToJoinTeam.rejected, (state, action: any) => {
        state.isLoading = false
        if (action.payload) {
          state.error = action.payload.errors || action.payload.message
        } else {
          state.error = action.error.message
        }
      })
      // Delete team
      .addCase(deleteTeam.pending, (state) => {
        state.isDeletingTeam = true
        state.error = null
      })
      .addCase(deleteTeam.fulfilled, (state, { payload, meta }) => {
        state.isDeletingTeam = false
        state.message = payload.message
        // Remove the team from the teams array
        const teamId = meta.arg
        state.teams = state.teams.filter((team) => team._id !== teamId)
        if (state.currentTeamId === teamId) {
          state.currentTeamId = null
        }
      })
      .addCase(deleteTeam.rejected, (state, action) => {
        state.isDeletingTeam = false
        if (action.payload) {
          state.error = action.payload.errors || action.payload.message
        } else {
          state.error = action.error.message
        }
      })
      // Admin delete team (errors are surfaced by the caller's toast)
      .addCase(adminDeleteTeam.fulfilled, (state, { meta }) => {
        const teamId = meta.arg
        state.teams = state.teams.filter((team) => team._id !== teamId)
        if (state.currentTeamId === teamId) {
          state.currentTeamId = null
        }
      })
      // Leave team
      .addCase(leaveTeam.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(leaveTeam.fulfilled, (state, { payload, meta }) => {
        state.isLoading = false
        state.message = payload.message

        // If team was disbanded or deleted (last member left), remove it from the teams array
        if (meta.arg.options.disband || payload.teamDeleted) {
          const teamId = meta.arg.teamId
          state.teams = state.teams.filter((team) => team._id !== teamId)
          if (state.currentTeamId === teamId) {
            state.currentTeamId = null
          }
        }
      })
      .addCase(leaveTeam.rejected, (state, action) => {
        state.isLoading = false
        if (action.payload) {
          state.error = action.payload.errors || action.payload.message
        } else {
          state.error = action.error.message
        }
      })
      .addCase(getCaptainPanelData.pending, (state) => {
        state.isLoadingCaptainPanel = true;
        state.error = null;
      })
      .addCase(getCaptainPanelData.fulfilled, (state, { payload }) => {
         state.isLoadingCaptainPanel = false;
         state.captainPanel = payload;
      })
      .addCase(getCaptainPanelData.rejected, (state, action) => {
        state.isLoadingCaptainPanel = false;
        if (action.payload) {
          // Use optional chaining to safely access properties
          state.error = (action.payload as any)?.errors || (action.payload as any)?.message;
        } else {
          state.error = action.error.message;
        }
      })
  },
})

export const { teamMessageCleanUp, clearTeamError } = teamSlice.actions
export const { reducer, actions } = teamSlice